import React, { useState, useRef, useCallback, useEffect } from 'react';
import { SHOTS, shotSrc, stepIndex, indexFromScroll } from '../lib/shots';
import { trackEvent } from '../lib/analytics';

// Screenshots of the plugin, swipeable. Shown to a Shopping click in place of
// the price grid: that visitor read the price in the ad and does not need it
// argued again, but has never seen the thing it buys.
//
// Scroll-snap rather than a carousel library. The browser already does momentum,
// snapping, keyboard scrolling and reduced-motion for free on an overflow
// container; JS here only mirrors where the scroll ended up so the dots and
// arrows agree with it.
// `shots` and `surface` exist because the ad landings show a different cut of
// the same screenshots. `surface` is not cosmetic: without it the ad carousel's
// scrolls would land in the same `shots_viewed` bucket as the Shopping page's
// and quietly corrupt the max(index)-per-session read this event was built for.
// `flat` is the homepage's look: one rounded card at a time, like the hero
// above it, no 16:9 frame, and the dots alone to move (no arrows).
export default function ShotsCarousel({ shots = SHOTS, surface = 'landing', flat = false }) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);

  // A swipe and a scroll produce no event of their own, so without this the
  // carousel is unmeasurable: seven Google clicks came through it and nothing
  // could tell "they never saw the screenshots" from "they saw them and moved
  // on". One `shots_viewed` per slide actually reached — max(index) over a
  // session is the depth, and the index-0 row is the denominator.
  //
  // Deduped per slide because a scroll fires this handler dozens of times and
  // a visitor swiping back and forth would otherwise inflate every count.
  const seenRef = useRef(new Set());
  const report = useCallback((i, via) => {
    if (i == null || !shots[i] || seenRef.current.has(i)) return;
    seenRef.current.add(i);
    trackEvent('shots_viewed', { index: i, shot: shots[i].id, via, total: shots.length, surface });
  }, [shots, surface]);

  // Index 0 is reported on visibility rather than on mount: the carousel sits
  // below the hero, so a session that never scrolled to it has not seen it, and
  // counting it as viewed would make every later rate look worse than it is.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof IntersectionObserver === 'undefined') {
      report(0, 'unobserved');
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        report(0, 'visible');
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(track);
    return () => io.disconnect();
  }, [report]);

  const goTo = useCallback((next, via) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[next];
    if (!slide) return;
    // scrollTo on the track, not scrollIntoView on the slide: the latter also
    // scrolls the page vertically to bring the section into view, which yanks
    // the visitor around when they tap an arrow mid-section.
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    setIndex(next);
    report(next, via);
  }, [report]);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[0];
    const next = indexFromScroll(track.scrollLeft, slide ? slide.offsetWidth : 0, shots.length);
    setIndex(next);
    // 'swipe' covers any scroll the visitor drove themselves. An arrow or a dot
    // scrolls the track too, but report() has already claimed that index by the
    // time this fires, so the label survives.
    //
    // One known bias: a dot that jumps 1 -> 3 smooth-scrolls over slide 2, which
    // lands here and records it as swiped. Deliberately not suppressed — the
    // metric this exists for is max(index) per session, which a pass-through
    // cannot change. Only the per-slide counts run slightly high, so read those
    // as "reached", not "looked at".
    report(next, 'swipe');
  }, [report]);

  return (
    <div className={flat ? "shots shots-flat" : "shots"}>
      <div className="shots-track" ref={trackRef} onScroll={onScroll}>
        {shots.map((shot, i) => (
          <figure className="shots-slide" key={shot.id}>
            <img
              src={shot.src || shotSrc(shot.id)}
              alt={shot.alt}
              // The first slide is what the section paints with, so it loads
              // eagerly; the other three are a swipe away at the earliest.
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </figure>
        ))}
      </div>

      <div className="shots-nav">
        {!flat && (
          <button
            type="button"
            className="shots-arrow"
            onClick={() => goTo(stepIndex(index, -1, shots.length), 'arrow')}
            disabled={index === 0}
            aria-label="Previous screenshot"
          >
            ‹
          </button>
        )}
        <div className="shots-dots">
          {shots.map((shot, i) => (
            <button
              type="button"
              key={shot.id}
              className={i === index ? 'shots-dot is-on' : 'shots-dot'}
              onClick={() => goTo(i, 'dot')}
              aria-label={shot.alt}
              aria-current={i === index}
            />
          ))}
        </div>
        {!flat && (
          <button
            type="button"
            className="shots-arrow"
            onClick={() => goTo(stepIndex(index, 1, shots.length), 'arrow')}
            disabled={index === shots.length - 1}
            aria-label="Next screenshot"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
}
